<template>
  <div class="dark-mode-table">
    <table>
      <colgroup>
        <col width="12%" />
        <col width="18%" />
        <col width="26%" />
        <col width="15%" />
        <col width="14%" />
        <col width="15%" />
      </colgroup>
      <thead>
        <tr>
          <th>{{ t('label_type') }}</th>
          <th>{{ t('label_title_column') }}</th>
          <th>{{ t('label_summary_content') }}</th>
          <th>{{ t('label_interpretation_date') }}</th>
          <th>{{ t('label_save_language') }}</th>
          <th>{{ t('status') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="script in scripts" :key="script.chatId">
          <!--타입-->
          <td>
            <div class="td-inr type">
              <div class="icon-box" :class="[script.chatType.toLowerCase()]">
                <img :src="`/images/mobile/icon/${script.chatType.toLowerCase()}-icon.svg`" alt="" />
              </div>
              <span>{{ getChatTitle(script.chatType) }}</span>
            </div>
          </td>

          <!--타이틀-->
          <td>
            <div class="td-inr">{{ script.chatName }}</div>
          </td>

          <!--요약내용-->
          <td>
            <div class="td-inr ag">
              <span>{{ script.title }}</span>
              <button type="button" class="tb-gray-btn auto-s-btn" @click="toggleTitleModal(script.chatId)">
                {{ t('label_change_short') }}
              </button>
            </div>
            <teleport to="body">
              <ModalAbridgementChange
                v-if="titleModalId === script.chatId"
                :script="script"
                @close="toggleTitleModal(script.chatId)"
                @confirm="requestChangeScriptTitle"
              />
            </teleport>
          </td>

          <!--통역 시작일자-->
          <td>
            <div class="td-inr">{{ formatDateTimeByLocale(script.createdAt) }}</div>
          </td>

          <!--저장언어-->
          <td>
            <div class="td-inr">
              <template v-if="script.status === 'CHAT_IN_PROGRESS'">
                <span>{{ t('label_chat_in_progress') }}</span>
                <img class="icon" src="/icons/ing-icon.svg" alt="" />
              </template>
              <div v-else class="scon-select">
                <select @change="requestScriptState(script.chatId, $event.target.value)">
                  <option
                    v-for="language in getSortedLanguages(script.languageCode)"
                    :key="language.code"
                    :value="language.code"
                  >
                    {{ language.name }}
                  </option>
                </select>
              </div>
            </div>
          </td>

          <!--상태-->
          <td>
            <div class="td-inr situation">
              <DownloadTableStatusButton :script="script" :language-code="script.languageCode" @update="updateScript" />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import DownloadTableStatusButton from '@/components/shared/composite/DownloadTableStatusButton.vue';
import ModalAbridgementChange from '@/components/shared/composite/ModalAbridgementChange.vue';
import useChatScriptDetail from '@/composables/domains/chat/useChatScriptDetail';
import { ChatScript } from '@/types/response';
import formatDateTimeByLocale from '@/utils/common/formatDateTimeByLocale';

withDefaults(
  defineProps<{
    scripts?: ChatScript[];
  }>(),
  {
    scripts: () => [],
  }
);

const emit = defineEmits<{ update: [chatId: string, updatedScript: Partial<ChatScript>] }>();

const { t } = useI18n();

const updateScript = (chatId: string, updatedScript: Partial<ChatScript>) => {
  emit('update', chatId, updatedScript);
};

const {
  titleModalId,
  getChatTitle,
  toggleTitleModal,
  getSortedLanguages,
  requestScriptState,
  requestChangeScriptTitle,
} = useChatScriptDetail(updateScript);
</script>

<style scoped lang="scss">
.dark-mode-table {
  margin-top: 1.5rem;
  max-height: 50vh;
  overflow-y: auto;
  span,
  p,
  div,
  select {
    color: var(--font-default);
  }
  table {
    width: 100%;
    min-width: 1000px;
    tr {
      th {
        height: 2rem;
        line-height: 2rem;
        font-size: $font-size-psmall;
        color: $color-gray-200;
        text-align: left;
        position: sticky;
        padding: 0.25rem;
        top: 0;
        background: #fff;
        z-index: 2;
        &:first-child {
          padding-left: 1rem;
          border-top-left-radius: 0.5rem;
          border-bottom-left-radius: 0.5rem;
        }
        &:last-child {
          border-top-right-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
        }
      }
    }
    tbody {
      tr {
        &:nth-child(odd) {
          background: #fafafb;
        }
        &:nth-child(even) {
          background: #fff;
        }
        td {
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
          padding: 0.25rem;
          font-size: $font-size-psmall;
          vertical-align: top;
          &:first-child {
            padding-left: 1rem;
          }
          .td-inr {
            min-height: 2.5rem;
            display: flex;
            align-items: center;
            .icon {
              margin-left: $size-xs;
            }
            select {
              background: $color-gray-20;
            }
          }
          .type {
            display: flex;
            height: 100%;
            align-items: center;
            gap: $size-s;
            .icon-box {
              width: 1.75rem;
              height: 1.75rem;
              border-radius: 12px;
              background: #fff;
              padding: 4px;
              img {
                width: 100%;
                height: 100%;
                object-fit: contain;
              }
              &.audience {
                border: 1px solid $audience-line;
              }
              &.conference {
                border: 1px solid $conference-line;
              }
              &.single {
                border: 1px solid $single-line;
              }
              &.general {
                border: 1px solid $general-line;
              }
              &.meeting {
                border: 1px solid $meeting-line;
              }
            }
          }
          .ag {
            gap: $size-s;
            justify-content: flex-start;
            span {
              line-height: 130%;
            }
          }
          .situation {
            gap: $size-s;
            .clear {
              color: var(--primary);
              svg {
                margin-left: $size-xs;
              }
            }
            div {
              display: flex;
              align-items: center;
            }
          }
        }
      }
    }
  }
}

.tb-gray-btn {
  background: $color-gray-20;
  color: var(--font-default);
}
.auto-m-btn {
  height: 2.5rem;
  padding: 0 1rem;
  border-radius: 8px;
  color: var(--font-default);
}
.auto-s-btn {
  height: 2rem;
  padding: 0 1rem;
  border-radius: 8px;
  color: var(--font-default);
}
.date-item {
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: url(/icons/calendar-icon.svg) no-repeat center;
    width: 20px;
    height: 20px;
    right: 12px;
  }
  :deep(.date-picker) {
    padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  }
}

.data-off {
  height: 100%;
  text-align: center;
  padding: 8rem 0;
  .v-img {
    margin-bottom: 1rem;
  }
}

@media (max-width: 768px) {
  .dwl-modal {
    .head {
      .date-wrap {
        width: 100%;
        gap: 0.25rem;
        .date-item {
          width: 100%;
        }
      }
    }
    .filter-num {
      margin-top: 0.5rem;
      background: #fafafb;
      padding: 0.5rem 0.75rem;
      border-radius: $size-s;
      p {
        font-size: $font-size-psmall;
        color: $color-gray-200;
        span {
          color: $color-gray-900;
        }
      }
    }
    .mob-list {
      li {
        padding: 1.25rem 0;
        border-bottom: 1px solid $color-gray-40;
        .inr-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          .left {
            display: flex;
            align-items: center;
            gap: $size-s;
            .icon-box {
              width: 2.5rem;
              height: 2.5rem;
              border-radius: $size-m;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: $size-s;
              &.audience {
                background: $audience-bg;
              }
              &.conference {
                background: $conference-bg;
              }
              &.single {
                background: $single-bg;
              }
              &.general {
                background: $general-bg;
              }
              &.meeting {
                background: $meeting-bg;
              }
              img {
                width: 100%;
                height: 100%;
                object-fit: contain;
              }
            }
            .tit-wrap {
              div {
                color: var(--font-default);
              }
              span {
                display: block;
                font-size: $font-size-xs;
                color: $color-gray-200;
                margin-top: $size-xs;
              }
            }
          }
          .right {
            .add-info {
              display: flex;
              align-items: center;
              gap: $size-xs;
              font-size: $font-size-xs;
            }
            .clear {
              color: var(--primary);
            }
          }
        }
        .ag {
          padding: 0.5rem 0 2rem;
          display: flex;
          gap: 0.5rem;
          align-items: center;
          justify-content: space-between;
          span {
            font-size: $font-size-psmall;
            line-height: 130%;
          }
        }
      }
    }
    .btn-wrap {
      display: flex;
      gap: $size-s;
      align-items: center;
      width: 100%;
      button {
        flex-shrink: inherit;
      }
      .scon-select {
        width: 80%;
        select {
          height: 2.5rem;
          width: 100%;
        }
      }
    }
  }

  .date-item {
    &::before {
      right: 10px;
    }
    :deep(.date-picker) {
      padding: 0.5rem 2rem 0.5rem 0.75rem;
    }
  }
  .data-off {
    padding: 4rem 0;
  }
}
</style>
